import React, {useEffect, useState} from 'react';
import { Row, Col, Menu, Dropdown } from 'antd';
import { MenuOutlined, UserOutlined, BellFilled, BellOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MenuItem from './components/menu/MenuItem';
import { useAuth } from './hooks/auth';
import type { MenuProps } from 'antd';
import formatDate from './utils/formatDate';
import { getNotificationText, getNotificationUrl } from './utils/notificationHelper';
import { deleteTestSession, getTestSession } from './utils/testSession';
import ConfirmationModal from './components/ConfirmationModal';

interface MainMenuProps {
  isAdmin: boolean;
  isAdminMode: boolean;
  setIsAdminMode: (val: boolean) => void;
  adminMenuItems: any[];
  noticesCount: number;
  notifications: any[];
  user: any;
  handleNotificationDropdownOpen: (open: boolean) => void;
  mainMenuItems: { label: string; to: string; roles?: number[] }[];

  unapprovedPrizeCount: number;
}

const MainMenu: React.FC<MainMenuProps> = ({
  isAdmin,
  isAdminMode,
  setIsAdminMode,
  adminMenuItems,
  noticesCount,
  notifications,
  user,
  handleNotificationDropdownOpen,
  mainMenuItems,
  unapprovedPrizeCount
}) => {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [isShowMenu, setIsShowMenu] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [nextUrl, setNextUrl] = useState('');

  const adminTargetUrl = isAdminMode ? '/learning/categories' : (adminMenuItems?.[0]?.to || '/');

  useEffect(()=>{
    setTimeout(()=>setIsShowMenu(true), 100);
  },[])

  const handleNavigationAttempt = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    const activeSession = getTestSession();
    const isTestPage = pathname.startsWith('/monthly-test/') || pathname.startsWith('/test/');
    if (!activeSession || !isTestPage || pathname === to) return;

    e.preventDefault();
    setNextUrl(to);
    setConfirmOpen(true);
  };

  const adminClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (adminTargetUrl) handleNavigationAttempt(e, adminTargetUrl);
    if (e.defaultPrevented) return;
    if (e.ctrlKey) return;
    setIsAdminMode(!isAdminMode);
  };

  const items: MenuProps['items'] = [
    notifications.length === 0 && unapprovedPrizeCount === 0
      ? { key: 'empty', label: "Уведомления отсутствуют", disabled: true }
      : null,
    unapprovedPrizeCount > 0
      ? {
        key: 'unapproved-prizes',
        label: (
          <a href='/admin/materials'>
            Требуется согласование <br />
            <b>{unapprovedPrizeCount} несогласованных вознаграждений</b>
          </a>
        ),
      }
      : null,
    ...notifications.map(notification => {
      const isUnread = notification.unread;
      const text = getNotificationText(notification);
      const notificationUrl = getNotificationUrl(notification);
      
      const date = formatDate(notification.createdAt);

      return {
        key: `notif-${notification.id}`,
        label: (
          <a href={notificationUrl}>
            {isUnread && (
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: 'rgb(164, 2, 7)', marginRight: 6 }} />
            )}
            <span style={{ fontWeight: isUnread ? 'bold' : 'normal' }}>
              {text}
              {date && <span style={{ color: '#888', fontSize: '12px', marginLeft: '8px' }}>— {date}</span>}
            </span>
          </a>
        ),
      };
    }),
  ].filter(Boolean);

  if (!isAuthenticated) return null;

  return (
    <Row style={{ width: '100%' }}>
      <Col span={16}>
        <Menu overflowedIndicator={<MenuOutlined style={{ color: 'white' }} />} theme="dark" mode="horizontal" style={{ backgroundColor: '#A40207' }}>
        {isShowMenu ? <>
          {isAdmin && isAdminMode ? (
            <>
              {adminMenuItems.filter(item => ['Материалы', 'Категории'].includes(item.label)).length > 0 && (
                <Menu.SubMenu key="content" title="Контент" style={{ color: 'white' }} className="menu-item">
                  {adminMenuItems.filter(item => ['Материалы', 'Категории'].includes(item.label)).map((item, key) => (
                    <MenuItem key={key} to={item.to} label={item.label} onClick={handleNavigationAttempt} />
                  ))}
                </Menu.SubMenu>
              )}
              {adminMenuItems.filter(item => ['Пользователи', 'Филиалы', 'Действующие акции'].includes(item.label)).length > 0 && (
                <Menu.SubMenu key="org" title="Оргструктура" style={{ color: 'white' }} className="menu-item">
                  {adminMenuItems.filter(item => ['Пользователи', 'Филиалы', 'Действующие акции'].includes(item.label)).map((item, key) => (
                    <MenuItem key={key} to={item.to} label={item.label} onClick={handleNavigationAttempt} />
                  ))}
                </Menu.SubMenu>
              )}
              {adminMenuItems.filter(item => ['Отчет', 'Отчет по продавцам'].includes(item.label)).length > 0 && (
                <Menu.SubMenu key="reports" title="Отчеты" style={{ color: 'white' }} className="menu-item">
                  {adminMenuItems.filter(item => ['Отчет', 'Отчет по продавцам'].includes(item.label)).map((item, key) => (
                    <MenuItem key={key} to={item.to} label={item.label} onClick={handleNavigationAttempt} />
                  ))}
                </Menu.SubMenu>
              )}
              {adminMenuItems.filter(item => !['Материалы', 'Категории', 'Пользователи', 'Филиалы', 'Действующие акции', 'Отчет', 'Отчет по продавцам'].includes(item.label)).map((item, key) => (
                <MenuItem key={key} to={item.to} label={item.label} onClick={handleNavigationAttempt} />
              ))}
            </>
          ) : (
            mainMenuItems
                .filter(item => !item.roles || user?.type === 'admin' || item.roles.includes(user?.role))
                .map((item, key) => (
                  <MenuItem key={key} to={item.to} label={item.label} onClick={handleNavigationAttempt} />
                ))
          )}
          </>:<MenuItem to={'/'} label={''} />}
        </Menu>
      </Col>
      <Col span={8}>
        <Menu overflowedIndicator={<UserOutlined style={{ fontSize: '16px', color: 'white' }} />} theme="dark" mode="horizontal" style={{ backgroundColor: '#A40207', display: 'flex', justifyContent: 'end' }}>
          {isShowMenu ? <>
            {user && (
            <Menu.Item className='menu-item'>
              <Dropdown menu={{ items }} placement="bottom" overlayStyle={{ maxWidth: 600 }} onOpenChange={handleNotificationDropdownOpen}>
                {noticesCount > 0
                  ? <BellFilled className="menu-link" style={{ fontSize: 19, position: 'relative', top: 3 }} />
                  : <BellOutlined className="menu-link" style={{ fontSize: 19, position: 'relative', top: 3 }} />}
              </Dropdown>
              {!!noticesCount && (
                <div style={{
                  position: 'absolute',
                  height: 15,
                  width: 15,
                  top: 18,
                  left: 27,
                  background: 'white',
                  borderRadius: 15,
                  color: '#a40207',
                  textAlign: 'center',
                  fontSize: 12,
                  lineHeight: '15px',
                }}>{noticesCount}</div>
              )}
            </Menu.Item>
          )}
          {!!(isAdmin && adminMenuItems.length) && (
            <Menu.Item className='menu-item'>
              <Link to={adminTargetUrl} className="menu-link" onClick={adminClick}>
                {isAdminMode ? 'Я пользователь' : 'Я админ'}
              </Link>
            </Menu.Item>
          )}
          {user.type !== 'registered' && (
            <MenuItem to={`https://liderprodag.ru/${user?.role > 0 ? 'admin' : ''}`} label={'Лидер Продаж'} onClick={handleNavigationAttempt} />
          )}
          <MenuItem to={'/profile'} label={<span><UserOutlined style={{ fontSize: '15px' }} /> Профиль</span>} onClick={handleNavigationAttempt} />
          </>:<MenuItem to={'/'} label={''} />}
        </Menu>
      </Col>
      <ConfirmationModal
        visible={confirmOpen}
        title="Вы уверены, что хотите выйти из теста?"
        description="Данные незавершенного теста в браузере будут очищены."
        confirmText="Да"
        cancelText="Нет"
        onConfirm={() => {
          deleteTestSession();
          setConfirmOpen(false);
          navigate(nextUrl);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </Row>
  );
};

export default MainMenu;
